import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { shadowFilter, transitionStyle, type TextOverlay } from '../model'
import styles from '../styles/text.module.scss'

export interface TextLayerProps {
  /** Only the captions visible at the current playhead. */
  texts: TextOverlay[]
  /** Project seconds. Entrances and exits are computed from this rather
   * than run as CSS animations: the caption has to be wherever the playhead
   * says it is when you scrub, and it has to match a burn-in that is also a
   * function of time — not of when a keyframe happened to start. */
  time: number
  selectedId: string | null
  onSelect: (id: string | null) => void
  /** Fires throughout a drag — the caption follows the cursor live. */
  onMove: (id: string, x: number, y: number) => void
  onEditText: (id: string, text: string) => void
  /** Off while previewing: a caption you can grab would fight the player. */
  interactive: boolean
}

/**
 * Captions drawn over the video, and the direct-manipulation surface for
 * them: click selects, drag moves, double-click edits in place.
 *
 * Everything is positioned in **percentages of this box**, because the box
 * is a proxy at whatever size the layout gives it while the burn-in
 * happens at master resolution. The same fractions drive both, so what you
 * line up here is what gets rendered — see ``TextOverlay``.
 */
export function TextLayer({
  texts,
  time,
  selectedId,
  onSelect,
  onMove,
  onEditText,
  interactive,
}: TextLayerProps) {
  const boxRef = useRef<HTMLDivElement>(null)
  const capRefs = useRef(new Map<string, HTMLDivElement>())
  /** Transition scale per caption, read by the fit pass below — the two
   * multiply into one transform, and measuring has to neutralise both. */
  const animScales = useRef(new Map<string, number>())
  const [editingId, setEditingId] = useState<string | null>(null)
  const [drag, setDrag] = useState<{ id: string; dxFrac: number; dyFrac: number } | null>(null)
  const dragMovedRef = useRef(false)
  /** Which centre lines the caption is currently locked to. Only ever set
   * during a drag — the guides are feedback for the gesture, not decoration
   * on a caption that happens to sit in the middle. */
  const [snapped, setSnapped] = useState({ x: false, y: false })

  /**
   * Shrink each caption until it fits on one line.
   *
   * Measured, not guessed, and the *same* one-shot scale the burn-in
   * applies (``text_overlay.py``): natural width over the limit, capped at
   * 1. A different rule there would fit a different amount of text than
   * what you framed here.
   *
   * Scaling the element beats changing font-size: no second layout pass,
   * and the centre anchor survives because the scale composes with the
   * existing translate.
   */
  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return

    const fitAll = () => {
      const limit = box.getBoundingClientRect().width * MAX_WIDTH_FRAC
      if (limit <= 0) return
      for (const [id, el] of capRefs.current) {
        // Measure unscaled — a caption mid-``pop`` is drawn smaller than it
        // is, and fitting to that would let it overflow once it lands.
        el.style.transform = 'translate(-50%,-50%)'
        const natural = el.getBoundingClientRect().width
        const fit = natural > limit ? limit / natural : 1
        const anim = animScales.current.get(id) ?? 1
        el.style.transform = `translate(-50%,-50%) scale(${String(fit * anim)})`
      }
    }

    fitAll()
    const observer = new ResizeObserver(fitAll)
    observer.observe(box)
    // Fonts land after first paint; measuring against the fallback face
    // would scale from the wrong width.
    void document.fonts.ready.then(fitAll)
    return () => {
      observer.disconnect()
    }
  }, [texts, time])

  // Leaving edit mode when the selection moves away keeps "one caption is
  // being typed into" true by construction.
  useEffect(() => {
    if (editingId && editingId !== selectedId) setEditingId(null)
  }, [editingId, selectedId])

  useEffect(() => {
    if (!drag) return
    const box = boxRef.current
    if (!box) return

    const onMouseMove = (e: MouseEvent) => {
      const rect = box.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      dragMovedRef.current = true
      // Recomputed from absolute cursor position every move, not
      // accumulated: accumulating drifts the caption over a long drag.
      const x = (e.clientX - rect.left) / rect.width - drag.dxFrac
      const y = (e.clientY - rect.top) / rect.height - drag.dyFrac
      const snap = snapToCentre(clamp01(x), clamp01(y))
      setSnapped({ x: snap.snapX, y: snap.snapY })
      onMove(drag.id, snap.x, snap.y)
    }
    const onMouseUp = () => {
      setDrag(null)
      setSnapped({ x: false, y: false })
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [drag, onMove])

  return (
    <div
      ref={boxRef}
      className={styles.layer}>
      {/* Centre guides. They appear only while a drag is locked to a middle,
          which is what makes them read as "you are aligned" rather than as
          a permanent grid. */}
      {snapped.x ? <div className={cn(styles.guide, styles.guideV)} aria-hidden /> : null}
      {snapped.y ? <div className={cn(styles.guide, styles.guideH)} aria-hidden /> : null}
      {texts.map((text) => {
        const selected = text.id === selectedId
        const editing = text.id === editingId
        // Frozen while editing or dragging: an entrance replaying under the
        // cursor makes the caption impossible to place.
        const anim =
          editing || drag?.id === text.id
            ? { opacity: 1, dx: 0, dy: 0, scale: 1 }
            : transitionStyle(text, time)
        animScales.current.set(text.id, anim.scale)
        return (
          <div
            key={text.id}
            ref={(el) => {
              if (el) capRefs.current.set(text.id, el)
              else capRefs.current.delete(text.id)
            }}
            className={cn(
              styles.text,
              interactive && styles.textGrabbable,
              selected && styles.textSelected,
              text.background === 'box' && styles.textBoxed,
            )}
            style={{
              // The slide travels as a fraction of the *frame*, so it goes
              // into left/top — a percentage inside ``transform`` would be a
              // fraction of the caption instead, and a long caption would
              // slide further than a short one.
              left: `${String((text.x + anim.dx) * 100)}%`,
              top: `${String((text.y + anim.dy) * 100)}%`,
              opacity: anim.opacity,
              // Size is a fraction of frame height, so it scales with the
              // box and matches what the burn-in computes from the master.
              fontSize: `${String(text.size * 100)}cqh`,
              fontFamily: `"${text.fontFamily}", sans-serif`,
              color: text.color,
              // Inline rather than a class: size and colour are per-caption
              // now, and this is the same string the burn-in builds.
              ...(text.background === 'shadow'
                ? { filter: shadowFilter(text.shadowSize, text.shadowColor) }
                : {}),
            }}
            onMouseDown={(e) => {
              if (!interactive || editing) return
              e.stopPropagation()
              e.preventDefault()
              const rect = boxRef.current?.getBoundingClientRect()
              if (!rect || rect.width === 0) return
              onSelect(text.id)
              dragMovedRef.current = false
              setDrag({
                id: text.id,
                // Grab offset, so the caption doesn't jump its centre to
                // the cursor on mousedown.
                dxFrac: (e.clientX - rect.left) / rect.width - text.x,
                dyFrac: (e.clientY - rect.top) / rect.height - text.y,
              })
            }}
            onDoubleClick={(e) => {
              if (!interactive) return
              e.stopPropagation()
              setEditingId(text.id)
              onSelect(text.id)
            }}
            // Keyboard equivalent of the whole interaction: arrows nudge the
            // position (the mouse can't land on an exact fraction), Enter
            // opens the editor, Escape deselects.
            role="button"
            tabIndex={interactive ? 0 : -1}
            aria-label={`Texto: ${text.text || '(vazio)'}`}
            onKeyDown={(e) => {
              if (!interactive || editing) return
              if (e.key === 'Enter') {
                e.preventDefault()
                onSelect(text.id)
                setEditingId(text.id)
                return
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                onSelect(null)
                return
              }
              const step = (e.shiftKey ? 0.05 : 0.01) as number
              const delta = NUDGE[e.key]
              if (!delta) return
              e.preventDefault()
              onSelect(text.id)
              onMove(text.id, clamp01(text.x + delta[0] * step), clamp01(text.y + delta[1] * step))
            }}
          >
            {editing ? (
              <TextInput
                value={text.text}
                onCommit={(next) => {
                  onEditText(text.id, next)
                  setEditingId(null)
                }}
                onCancel={() => {
                  setEditingId(null)
                }}
              />
            ) : (
              // Preserves the blank line a user typed, and stops an empty
              // caption from collapsing to an ungrabbable zero-size box.
              (text.text || ' ')
            )}
          </div>
        )
      })}
    </div>
  )
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

/**
 * How close to a centre line a drag has to get before it locks on, as a
 * fraction of the frame. Roughly 1% — wide enough that you can't miss it by
 * hand, narrow enough that a caption deliberately placed just off centre
 * still lands where you put it.
 */
export const SNAP_THRESHOLD = 0.012

/**
 * Lock a dragged caption to the frame's centre lines.
 *
 * Both axes independently, so the two combine into a true centre without
 * needing a third rule. Mouse only: on the arrow keys a snap would swallow
 * the step and make it impossible to nudge *past* the middle, which is the
 * one thing keyboard positioning is for.
 */
export function snapToCentre(
  x: number,
  y: number,
  threshold = SNAP_THRESHOLD,
): { x: number; y: number; snapX: boolean; snapY: boolean } {
  const snapX = Math.abs(x - 0.5) <= threshold
  const snapY = Math.abs(y - 0.5) <= threshold
  return { x: snapX ? 0.5 : x, y: snapY ? 0.5 : y, snapX, snapY }
}

/** Share of the frame width a caption may occupy before it shrinks.
 * Mirrored in ``text_overlay.py`` — a different limit there would fit a
 * different amount of text than the preview showed. */
const MAX_WIDTH_FRAC = 0.92

/** Arrow key → direction on the frame, as [x, y] multipliers. */
const NUDGE: Record<string, [number, number] | undefined> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
}

interface TextInputProps {
  value: string
  onCommit: (value: string) => void
  onCancel: () => void
}

/** Edits in place, inheriting the caption's own typography so the text
 * doesn't change shape the moment you start typing. */
function TextInput({ value, onCommit, onCancel }: TextInputProps) {
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.focus()
    el.select()
  }, [])

  return (
    <textarea
      ref={ref}
      className={styles.textInput}
      value={draft}
      rows={1}
      onChange={(e) => {
        setDraft(e.target.value)
      }}
      onBlur={() => {
        onCommit(draft)
      }}
      onMouseDown={(e) => {
        // Selecting text inside the editor must not start a drag of the
        // caption underneath.
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        } else if (e.key === 'Enter' && !e.shiftKey) {
          // Enter commits; Shift+Enter is a line break, which captions use.
          e.preventDefault()
          onCommit(draft)
        }
      }}
      aria-label="Editar o texto"
    />
  )
}
